import CellWithEllipsis from "src/components/TableCellComponents/CellWithEllipsis";
import { HeaderRule } from "../interface";
import AdjExtRetal from "./cellComponents/AdjExtRetal";
import Cost from "./cellComponents/Cost";
import InputItem from "./cellComponents/InputItem";
import NumericInputWithMax, {
  NUMERIC_INPUT_DEFAULT_MAX
} from "./cellComponents/NumericInputWithMax";
import LinkItem from "./cellComponents/LinkItem";
import ThresholdValue from "./cellComponents/ThresholdValue";
import { Box, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import PrependCurrency from "./cellComponents/PrependCurrency";
import Wishlist from "./cellComponents/Wishlist";
import { SplitCell } from "src/components/GoTable/components/cellComponents/SplitCell";
import DollarInputItem from "./cellComponents/DollarInputItem";
import CurrencyData from "./cellComponents/currencyData";
import ItemWithoutLink from "src/components/GoTable/components/cellComponents/ItemWithoutLink";
import UpcLink from "src/components/GoTable/components/cellComponents/UpcLink";
import GoItemWithInfoToolTip from "src/components/GoItemWithInfoToolTip/GoItemWithInfoToolTip";
import { IRowDataStoreCreditDetails } from "src/utils/storeCreditUtils";
import stringToNumber from "src/utils/stringToNumber";
import ProduceCommodities from "./cellComponents/produceCommodities";
import TwoLineTextDisplay from "./cellComponents/TwoLineTextDisplay";
import HighlightValue from "./cellComponents/highlightValue";
import { VerticalInputPair } from "./cellComponents/VerticalInputPair";
import { DisplayWithInput } from "./cellComponents/DisplayWithInput";
import {
  roundNumber,
  handleNumericValue,
  resolveNumericMaxFromRowKey
} from "src/utils/genericFunctions";
import PackSizeInput from "./cellComponents/PackSizeInput";
import InputPMR from "./cellComponents/InputPMR";
import DescriptionWithBadge from "src/components/TableCellComponents/descriptionWithBadge";
import theme from "src/themes";

// Use the table header's id as key for each entry.
// If the cell does not require any formatting, just pass the cell data as is.
// Each entry will get the entire row data passed to it

const getInputComponent = (
  headerItem: HeaderRule,
  rowData: any,
  disableDecimalDigit = false,
  sxConfig = {}
) => (
  <InputItem
    rowData={rowData}
    id={headerItem?.id}
    itemId={rowData?.itemId}
    editMode={headerItem?.editMode}
    onBlur={headerItem?.handleBlur}
    disabled={rowData?.inputDisabled?.[headerItem?.id]}
    disableDecimalDigit={disableDecimalDigit}
    sxConfig={sxConfig}
  />
);

const inputComponentForPMR = (headerItem: HeaderRule, rowData: any) => {
  rowData[headerItem?.id] = headerItem?.editMode
    ? rowData[headerItem?.id]
    : handleNumericValue(+rowData[headerItem?.id]);
  return (
    <InputPMR
      rowData={rowData}
      id={headerItem?.id}
      itemId={rowData?.itemId}
      editMode={headerItem?.editMode}
      onBlur={headerItem?.handleBlur}
      disabled={rowData?.inputDisabled?.[headerItem?.id]}
      disableDecimalDigit={false}
      sxConfig={{
        "& .MuiOutlinedInput-input": {
          padding: "4px"
        },
        height: "34px",
        width: "95%",
        fontSize: "14px"
      }}
    />
  );
};

const getDollarInputComponent = (headerItem: HeaderRule, rowData: any) => (
  <DollarInputItem
    rowData={rowData}
    headerId={headerItem?.id}
    handleChange={headerItem?.handleChange}
    editMode={headerItem?.editMode}
  />
);
/* istanbul ignore next */
const ComponentsMap: { [key: string]: Function } = {
  variance: (rowData: any) => (
    <ThresholdValue
      value={rowData?.variance?.variance}
      highlighted={rowData?.variance?.highlighted}
    />
  ),
  itemId: (headerItem: HeaderRule, rowData: any) => {
    const query = rowData.receivingStoreId
      ? `?receivingStoreId=${rowData.receivingStoreId}`
      : "";
    return (
      <LinkItem
        rowData={rowData}
        id={headerItem?.id}
        navLink={`/product/${rowData[headerItem?.id]}${query}`}
      />
    );
  },
  itemWithoutLink: (headerItem: HeaderRule, rowData: any) => (
    <ItemWithoutLink value={rowData?.itemId?.toString().replace(/^0+/, "")} />
  ),
  invbeerWine: getDollarInputComponent,
  invgrocery: getDollarInputComponent,
  invfrozen: getDollarInputComponent,
  invdeli: getDollarInputComponent,
  invhbc: getDollarInputComponent,
  invgm: getDollarInputComponent,
  invproduce: getDollarInputComponent,
  invmeat: getDollarInputComponent,
  invvaluation: getDollarInputComponent,
  invtotal: getDollarInputComponent,
  received: getInputComponent,
  receivedEaches: (headerItem: HeaderRule, rowData: any) =>
    getInputComponent(headerItem, rowData, !rowData?.isRandomWeight),
  receivedCases: (headerItem: HeaderRule, rowData: any) =>
    getInputComponent(headerItem, rowData, true),
  over: (headerItem: HeaderRule, rowData: any) => {
    // For shipper item, disable all inputs, only the component item values can be modified
    const isShipperItem = rowData.isPalletMod && rowData.subItems.length > 0;

    return (
      <SplitCell
        rowData={rowData}
        headerItem={headerItem}
        editMode={headerItem.editMode}
        onBlur={headerItem?.handleBlur}
        blockedSymbols={
          rowData?.isRandomWeight
            ? ["e", "E", "+", "-"]
            : ["e", "E", "+", "-", "."]
        }
        disableAllInputs={isShipperItem}
      />
    );
  },
  short: (headerItem: HeaderRule, rowData: any) => {
    // For shipper item, disable all inputs, only the component item values can be modified
    const isShipperItem = rowData.isPalletMod && rowData.subItems.length > 0;

    return (
      <SplitCell
        rowData={rowData}
        headerItem={headerItem}
        editMode={headerItem.editMode}
        onBlur={headerItem?.handleBlur}
        blockedSymbols={
          rowData?.isRandomWeight
            ? ["e", "E", "+", "-"]
            : ["e", "E", "+", "-", "."]
        }
        disableAllInputs={isShipperItem}
      />
    );
  },
  adjustedExtendedRetail: (_headerItem: HeaderRule, rowData: any) => (
    <AdjExtRetal rowData={rowData} />
  ),
  cost: (_headerItem: HeaderRule, rowData: any) => <Cost rowData={rowData} />,
  description: (headerItem: HeaderRule, rowData: any) => (
    <CellWithEllipsis rowData={rowData} id={headerItem?.id} />
  ),
  descriptionWithBadge: (headerItem: HeaderRule, rowData: any) => (
    <DescriptionWithBadge rowData={rowData} id={"description"} />
  ),
  itemHash: (_headerItem: any, rowData: any) => (
    <Link to={`/product/${rowData.itemHash}`} aria-label={"product"}>
      <Typography variant="subtitle2" color={"primary.main"}>
        {rowData.itemHash}
      </Typography>
    </Link>
  ),
  upcLink: (_headerItem: any, rowData: any) => <UpcLink rowData={rowData} />,
  itemHashWithoutLink: (_headerItem: any, rowData: any) => (
    <ItemWithoutLink value={rowData?.itemHash?.toString().replace(/^0+/, "")} />
  ),
  bw: (headerItem: HeaderRule, rowData: any) => (
    <PrependCurrency value={rowData.bw} />
  ),
  netSales: (headerItem: HeaderRule, rowData: any) => (
    <PrependCurrency value={rowData.netSales} />
  ),
  deposits: (headerItem: HeaderRule, rowData: any) => (
    <PrependCurrency value={rowData.deposits} />
  ),
  /* istanbul ignore next */
  operatorVCA: (headerItem: HeaderRule, rowData: any) => (
    <PrependCurrency value={rowData.operatorVCA} />
  ),
  /* istanbul ignore next */
  coupons: (headerItem: HeaderRule, rowData: any) => (
    <PrependCurrency value={rowData.coupons} />
  ),
  grocery: (headerItem: HeaderRule, rowData: any) => (
    <PrependCurrency value={rowData.grocery} />
  ),
  frozen: (headerItem: HeaderRule, rowData: any) => (
    <PrependCurrency value={rowData.frozen} />
  ),
  gm: (headerItem: HeaderRule, rowData: any) => (
    <PrependCurrency value={rowData.gm} />
  ),
  hbc: (headerItem: HeaderRule, rowData: any) => (
    <PrependCurrency value={rowData.hbc} />
  ),
  meat: (headerItem: HeaderRule, rowData: any) => (
    <PrependCurrency value={rowData.meat} />
  ),
  deli: (headerItem: HeaderRule, rowData: any) => (
    <PrependCurrency value={rowData.deli} />
  ),
  produce: (headerItem: HeaderRule, rowData: any) => (
    <PrependCurrency value={rowData.produce} />
  ),
  salesTax: (headerItem: HeaderRule, rowData: any) => (
    <PrependCurrency value={rowData.salesTax} />
  ),
  wishlist: (headerItem: HeaderRule, rowData: any) => (
    <Wishlist rowData={rowData} handleClick={headerItem.clickAction} />
  ),
  damagedQuantity: (headerItem: HeaderRule, rowData: any) => {
    // For shipper item, disable all inputs, only the component item values can be modified
    const isShipperItem = rowData.isPalletMod && rowData.subItems.length > 0;

    return (
      <SplitCell
        rowData={rowData}
        headerItem={headerItem}
        editMode={rowData?.enableEdit}
        onBlur={headerItem?.handleBlur}
        blockedSymbols={
          rowData?.isRandomWeight
            ? ["e", "E", "+", "-"]
            : ["e", "E", "+", "-", "."]
        }
        disableAllInputs={isShipperItem}
      />
    );
  },
  landed: (headerItem: HeaderRule, rowData: any) => (
    <PrependCurrency value={stringToNumber(rowData.landed)} />
  ),
  extendedRetail: (_headerItem: HeaderRule, rowData: any) => (
    <CurrencyData value={rowData?.extendedRetail} />
  ),
  receivedCasesDsd: (_headerItem: HeaderRule, rowData: any) => (
    <ThresholdValue
      value={rowData?.receivedCasesDsd}
      highlighted={rowData?.highlightReceivedCases}
    />
  ),
  receivedCreateEdit: (
    _headerItem: HeaderRule,
    rowData: IRowDataStoreCreditDetails
  ) => (
    <GoItemWithInfoToolTip
      value={rowData?.received}
      condition={rowData?.isFCOrOSAdded}
      title={rowData?.fCOrOSTooltip}
      translationProps={rowData?.fCOrOSTooltipTranslationProps}
    />
  ),
  packSize: (headerItem: HeaderRule, rowData: any) =>
    headerItem?.editMode ? (
      <PackSizeInput headerItem={headerItem} rowData={rowData} />
    ) : (
      <TwoLineTextDisplay line1Text={rowData?.pack} line2Text={rowData.size} />
    ),
  casesPerThousand: inputComponentForPMR,
  masterDisplay: inputComponentForPMR,
  itemDescription: (headerItem: any, rowData: any) => (
    <ProduceCommodities
      itemId={rowData?.itemId || ""}
      description={rowData?.description || ""}
    />
  ),
  soldCases: (_: HeaderRule, rowData: any) => {
    const totalCasesSold =
      rowData.totalCasesSold === null
        ? "-"
        : handleNumericValue(rowData.totalCasesSold);
    const soldCasesPer1000 =
      rowData.soldCasesPer1000 === null
        ? null
        : handleNumericValue(rowData.soldCasesPer1000);
    return (
      <Box
        width="100%"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "4px"
        }}
      >
        <HighlightValue
          value={totalCasesSold.toString()}
          color={theme.palette.utility.black}
          backgroundColor={theme.palette.highlight.blue.background}
        />
        <HighlightValue
          value={soldCasesPer1000 ? soldCasesPer1000.toString() + "c" : "-"}
          color={theme.palette.highlight.blue.text}
          backgroundColor={theme.palette.highlight.blue.background}
        />
      </Box>
    );
  },
  costInput: (headerItem: HeaderRule, rowData: any) => (
    <NumericInputWithMax
      rowData={rowData}
      id={headerItem.id}
      itemId={rowData?.itemId}
      editMode={headerItem.editMode}
      onBlur={headerItem.handleBlur}
      disabled={rowData?.inputDisabled?.[headerItem.id]}
      disableDecimalDigit={false}
      sxConfig={{}}
      numericMax={
        resolveNumericMaxFromRowKey(
          rowData,
          headerItem.numericMaxFromRowKey
        ) ?? NUMERIC_INPUT_DEFAULT_MAX
      }
      errorMessage={"manualTransfer.poItems.costError"}
    />
  ),
  retailInput: (headerItem: HeaderRule, rowData: any) =>
    getInputComponent(headerItem, rowData)
};

const ids = Array.from({ length: 7 }, (_, index) => `${index}`);

for (const id of ids) {
  const osId = `${id}-over/short`;
  const csSoldId = `${id}-casesSold`;
  const inventoryId = `${id}-inventory`;
  const orderId = `${id}-order`;
  const prodAvailId = `${id}-prodAvail`;
  const prodReqId = `${id}-prodReq`;

  ComponentsMap[osId] = (_: HeaderRule, rowData: any) => {
    const value =
      rowData[osId] === null ? "-" : roundNumber(rowData[osId], 0, 0);
    return (
      <HighlightValue
        value={value?.toString()}
        color={theme.palette.highlight.neutral.text}
        backgroundColor={theme.palette.highlight.neutral.background}
      />
    );
  };
  ComponentsMap[csSoldId] = (_: HeaderRule, rowData: any) => {
    const value =
      rowData[csSoldId] === null ? "-" : roundNumber(rowData[csSoldId], 0, 0);
    return (
      <HighlightValue
        value={value?.toString()}
        highlighted={Number(rowData[csSoldId]) < 0}
        backgroundColor={theme.palette.highlight.green.background}
        color={theme.palette.highlight.green.text}
      />
    );
  };
  ComponentsMap[prodAvailId] = (_: HeaderRule, rowData: any) => {
    const value =
      rowData[prodAvailId] === null
        ? "-"
        : roundNumber(rowData[prodAvailId], 0, 0);
    return (
      <HighlightValue
        value={value?.toString()}
        color={theme.palette.highlight.red.text}
        backgroundColor={theme.palette.highlight.red.background}
      />
    );
  };
  ComponentsMap[prodReqId] = (_: HeaderRule, rowData: any) => {
    const value =
      rowData[prodReqId] === null ? "-" : roundNumber(rowData[prodReqId], 0, 0);
    return (
      <HighlightValue
        value={value?.toString()}
        color={theme.palette.highlight.neutral.text}
        backgroundColor={theme.palette.highlight.neutral.background}
      />
    );
  };
  ComponentsMap[inventoryId] = VerticalInputPair;
  ComponentsMap[orderId] = DisplayWithInput;
}

export { ComponentsMap };
